<template>
  <v-card flat class="px-6">
    <v-card-text>
      <v-form ref="form">
        <div class="d-flex align-center">
          <v-autocomplete
            v-model="selectedContractor"
            :items="filteredContractors"
            :loading="loading"
            :search-input.sync="search"
            item-text="displayName"
            item-value="id"
            return-object
            :label="$t('tslOrders.fields.selectCarrier')"
            :rules="[v => !!v || $t('tslOrders.messages.carrierRequired')]"
            :no-data-text="noDataText"
            :placeholder="'Wpisz minimum 3 znaki...'"
            clearable
            outlined
            class="flex-grow-1"
            @change="onContractorChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.companyName }} ({{ item.city }})</v-list-item-title>
                <v-list-item-subtitle>{{ item.vatNumber }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-btn
            v-if="selectedContractor"
            icon
            color="primary"
            class="ml-2"
            @click="openContractorDetails"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
        <v-btn
          text
          color="primary"
          class="mt-2"
          @click="showAddDialog = true"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('tslOrders.addNewCarrier') }}
        </v-btn>

        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="carrierFullName"
              :label="$t('tslOrders.fields.carrierFullName')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="carrierPhone"
              :label="$t('tslOrders.fields.carrierPhone')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="carrierPrice"
              :label="$t('tslOrders.fields.carrierPrice')"
              type="number"
              step="0.01"
              outlined
              :rules="[
                v => !!v || $t('tslOrders.messages.carrierPriceRequired'),
                v => v > 0 || $t('tslOrders.messages.priceMustBeGreaterThanZero'),
                v => !v || /^\d+(\.\d{1,2})?$/.test(String(v)) || $t('tslOrders.messages.priceMaxTwoDecimalPlaces')
              ]"
              required
              @input="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="carrierCurrency"
              :items="allCurrencies"
              item-text="description"
              item-value="id"
              :label="$t('tslOrders.fields.carrierCurrency')"
              outlined
              :rules="[v => v !== null && v !== undefined || $t('tslOrders.messages.carrierCurrencyRequired')]"
              required
              @change="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="carrierEmail"
              :label="$t('tslOrders.fields.carrierEmail')"
              type="email"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="carrierVehicleType"
              :label="$t('tslOrders.fields.carrierVehicleType')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="carrierRegistrationNumber"
              :label="$t('tslOrders.fields.carrierRegistrationNumber')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="carrierPaymentType"
              :items="paymentTypeOptions"
              item-text="text"
              item-value="value"
              :label="$t('tslOrders.fields.carrierPaymentType')"
              outlined
              clearable
              @change="emitAdditionalFields"
            />
          </v-col>
          <v-col v-if="carrierPaymentType === 1" cols="12" md="6">
            <v-text-field
              v-model.number="carrierPaymentDays"
              :label="$t('tslOrders.fields.carrierPaymentDays')"
              type="number"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <RichTextEditor
              v-model="carrierNotes"
              :label="$t('tslOrders.fields.carrierNotes')"
              toolbar-type="simple"
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <AddTSLContractor
      v-model="showAddDialog"
      :dialog-mode="true"
      :contractor-type-override="2"
      @created="onContractorCreated"
      @close="showAddDialog = false"
    />
  </v-card>
</template>

<script>
import Service from '@/services'
import AddTSLContractor from '@/views/TSL/Contractors/AddTSLContractor'
import RichTextEditor from '@/components/forms/RichTextEditor'

export default {
  name: 'CarrierTab',
  components: {
    AddTSLContractor,
    RichTextEditor
  },
  props: {
    value: {
      type: String,
      default: null
    },
    additionalFields: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      selectedContractor: null,
      contractors: [],
      loading: false,
      search: null,
      showAddDialog: false,
      carrierFullName: this.additionalFields.carrierFullName || '',
      carrierPhone: this.additionalFields.carrierPhone || '',
      carrierEmail: this.additionalFields.carrierEmail || '',
      carrierPrice: this.additionalFields.carrierPrice || null,
      carrierCurrency: this.additionalFields.carrierCurrency ?? 0,
      carrierVehicleType: this.additionalFields.carrierVehicleType || '',
      carrierNotes: this.additionalFields.carrierNotes || '',
      carrierRegistrationNumber: this.additionalFields.carrierRegistrationNumber || '',
      carrierPaymentType: this.additionalFields.carrierPaymentType || null,
      carrierPaymentDays: this.additionalFields.carrierPaymentDays || null,
      paymentTypeOptions: [
        { value: 0, text: this.$t('tslOrders.paymentTypes.none') },
        { value: 1, text: this.$t('tslOrders.paymentTypes.deferred') },
        { value: 2, text: this.$t('tslOrders.paymentTypes.paymentOnUnloading') },
        { value: 3, text: this.$t('tslOrders.paymentTypes.paymentInAdvance') }
      ]
    }
  },
  computed: {
    allCurrencies () {
      return this.$store.getters.allCurrencies
    },
    filteredContractors () {
      // Zawsze dodaj wybrany element, jeśli istnieje
      if (this.selectedContractor) {
        if (!this.search || this.search.length < 3) {
          return [this.selectedContractor]
        }
        // Jeśli wybrany element nie jest w wynikach wyszukiwania, dodaj go
        const hasSelected = this.contractors.some(c => c.id === this.selectedContractor.id)
        if (!hasSelected) {
          return [this.selectedContractor, ...this.contractors]
        }
      }
      if (!this.search || this.search.length < 3) {
        return []
      }
      return this.contractors
    },
    noDataText () {
      if (!this.search || this.search.length < 3) {
        return 'Wpisz minimum 3 znaki'
      }
      return 'Brak wyników'
    }
  },
  watch: {
    search (val) {
      if (val && val.length >= 3) {
        this.loadContractors()
      } else {
        this.contractors = []
      }
    },
    value: {
      immediate: true,
      handler (newVal) {
        if (newVal && !this.selectedContractor) {
          this.loadContractorById(newVal)
        }
      }
    },
    additionalFields: {
      deep: true,
      handler (newVal) {
        if (newVal) {
          this.carrierFullName = newVal.carrierFullName || ''
          this.carrierPhone = newVal.carrierPhone || ''
          this.carrierEmail = newVal.carrierEmail || ''
          this.carrierPrice = newVal.carrierPrice || null
          this.carrierCurrency = newVal.carrierCurrency ?? 0
          this.carrierVehicleType = newVal.carrierVehicleType || ''
          this.carrierNotes = newVal.carrierNotes || ''
          this.carrierRegistrationNumber = newVal.carrierRegistrationNumber || ''
          this.carrierPaymentType = newVal.carrierPaymentType || null
          this.carrierPaymentDays = newVal.carrierPaymentDays || null
        }
      }
    },
    carrierPaymentType (newVal) {
      // Clear payment days if not deferred
      if (newVal !== 1) {
        this.carrierPaymentDays = null
        this.emitAdditionalFields()
      }
    }
  },
  mounted () {
    // Load contractors only when searching (min 3 characters)
  },
  methods: {
    async loadContractors () {
      this.loading = true
      try {
        const { data } = await Service.get('/TSLContractor/GetContractors', {
          type: 2, // Carrier type
          page: 1,
          pageSize: 100,
          search: this.search
        })
        this.contractors = data.results.map(c => ({
          id: c.id,
          companyName: c.companyName,
          vatNumber: c.vatNumber,
          city: c.city,
          displayName: `${c.companyName} (${c.vatNumber})`
        }))
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error loading carriers')
      } finally {
        this.loading = false
      }
    },
    async loadContractorById (id) {
      try {
        const { data } = await Service.get('/TSLContractor/GetContractorDetails', { id })
        this.selectedContractor = {
          id: data.id,
          companyName: data.companyName,
          vatNumber: data.vatNumber,
          street: data.street,
          zipCode: data.zipCode,
          city: data.city,
          country: data.country,
          displayName: `${data.companyName} (${data.vatNumber})`,
          paymentType: data.paymentType ?? null,
          deferredPaymentDays: data.deferredPaymentDays ?? null
        }
      } catch (e) {
        console.error('Error loading contractor:', e)
      }
    },
    async onContractorChange () {
      if (this.selectedContractor?.id) {
        await this.loadContractorById(this.selectedContractor.id)
        if (this.selectedContractor.paymentType != null) {
          this.carrierPaymentType = this.selectedContractor.paymentType
        }
        if (this.selectedContractor.deferredPaymentDays != null) {
          this.carrierPaymentDays = this.selectedContractor.deferredPaymentDays
        }
        this.emitAdditionalFields()
      }
      this.$emit('update', this.selectedContractor)
      this.$emit('input', this.selectedContractor?.id || null)
    },
    openContractorDetails () {
      if (this.selectedContractor) {
        const route = this.$router.resolve({
          name: 'TSLContractorDetails',
          params: { id: this.selectedContractor.id }
        })
        window.open(route.href, '_blank')
      }
    },
    async onContractorCreated (contractorData) {
      // Load full details of the newly created contractor
      await this.loadContractorById(contractorData.id)
      this.$emit('update', this.selectedContractor)
      this.$emit('input', contractorData.id)
      this.showAddDialog = false
    },
    emitAdditionalFields () {
      this.$emit('update-additional', {
        carrierFullName: this.carrierFullName,
        carrierPhone: this.carrierPhone,
        carrierEmail: this.carrierEmail,
        carrierPrice: this.carrierPrice,
        carrierCurrency: this.carrierCurrency,
        carrierVehicleType: this.carrierVehicleType,
        carrierNotes: this.carrierNotes,
        carrierRegistrationNumber: this.carrierRegistrationNumber,
        carrierPaymentType: this.carrierPaymentType,
        carrierPaymentDays: this.carrierPaymentDays
      })
    }
  }
}
</script>
